import { queryKeyFn } from '@/generated/http-clients/v3ApiContext'
import type { OrgSearchResult } from '@/generated/http-clients/v3ApiSchemas'
import { ORG_SEARCH_MIN_LENGTH, type OrgSearchState } from '@/helpers/orgSearch'
import type { QueryKey } from '@tanstack/react-query'

export const ORG_PICKER_LIMIT = 10
export const ORG_PICKER_DEBOUNCE_MS = 300

export type OrgPickerStatus =
  | { kind: 'none' }
  | { kind: 'tooShort' }
  | { kind: 'loading' }
  | { kind: 'empty'; query: string }
  | { kind: 'unconfirmed' }
  | { kind: 'truncated' }
  | { kind: 'invalid'; message?: string }
  | { kind: 'unavailable'; canRetry: boolean }

export function orgPickerQueryKey(q: string): QueryKey {
  return queryKeyFn({
    path: '/api/v3/search/orgs',
    operationId: 'searchOrganizations',
    variables: { queryParams: { q, limit: ORG_PICKER_LIMIT } },
  })
}

export function findExactOrg(
  input: string,
  results: OrgSearchResult[],
): OrgSearchResult | undefined {
  const id = input.trim()
  return results.find((result) => result.id === id)
}

export function deriveOrgPickerStatus(
  input: string,
  term: string,
  searchState: OrgSearchState,
): OrgPickerStatus {
  const trimmed = input.trim()
  if (trimmed === '') {
    return { kind: 'none' }
  }
  if (trimmed.length < ORG_SEARCH_MIN_LENGTH) {
    return { kind: 'tooShort' }
  }
  if (trimmed !== term) {
    return { kind: 'loading' }
  }
  return statusFromSearchState(term, searchState)
}

function statusFromSearchState(
  term: string,
  searchState: OrgSearchState,
): OrgPickerStatus {
  switch (searchState.kind) {
    case 'idle':
      return { kind: 'none' }
    case 'tooShort':
      return { kind: 'tooShort' }
    case 'loading':
      return { kind: 'loading' }
    case 'empty':
      return { kind: 'empty', query: term }
    case 'results':
      return searchState.truncated
        ? { kind: 'truncated' }
        : { kind: 'unconfirmed' }
    case 'invalid':
      return { kind: 'invalid', message: searchState.message }
    case 'disabled':
      return { kind: 'unavailable', canRetry: false }
    case 'error':
      return { kind: 'unavailable', canRetry: true }
  }
}
